import { useEffect, useRef, useState } from "react";
import { getApiSearchListingAutocomplete, getApiSearchAutocomplete, type Autocomplete } from "../../../api/search-client";
import { MapPin, X } from 'lucide-react';
import { capitalizeFirstLetters } from '../../../util/AddressUtil';

interface Suggestion {
    id: string;
    localityName: string;
    state: string;
    postcode: string;
}

interface AddressStepProps {
    onLocalitySelect: (localityId: string | null, localityName: string) => void;
    onAddressValidityChange: (isValid: boolean) => void;
    onAddressSelect: (addressId: string | null, address: string, localityId: string) => void;
    savedLocalityId?: string | null;
    savedLocalityName?: string | null;
    savedAddressId?: string | null;
    savedAddressName?: string | null;
}

function createInitialLocalitySuggestion(id?: string, localityName?: string): Suggestion | null {
    // Return null if both parameters are missing
    if (id === undefined && localityName === undefined) {
      return null;
    }
    
    // Create a Suggestion with the provided values
    // and empty strings for the rest
    return {
      id: id || '',
      localityName: localityName || '',
      state: '',
      postcode: ''
    };
  }

const AddressStep: React.FC<AddressStepProps> = ({
    onLocalitySelect,
    onAddressValidityChange,
    onAddressSelect,
    savedLocalityId,
    savedLocalityName,
    savedAddressId,
    savedAddressName
}) => {
    // Suburb state
    const [suburbInput, setSuburbInput] = useState(capitalizeFirstLetters(savedLocalityName || ''));
    const [suburbSuggestions, setSuburbSuggestions] = useState<Suggestion[]>([]);
    const [selectedSuburb, setSelectedSuburb] = useState<Suggestion | null>(createInitialLocalitySuggestion(savedLocalityId, savedLocalityName));
    const [isSuburbFocused, setIsSuburbFocused] = useState(false);
    
    // Street address state
    const [suggestions, setSuggestions] = useState<Autocomplete[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [lastQuery, setLastQuery] = useState('');
    const [selectedAddress, setSelectedAddress] = useState<Autocomplete | null>(null);
    const [isFirstAttempt, setIsFirstAttempt] = useState(true);
    const [isValidAddress, setIsValidAddress] = useState(false);
    const [inputValue, setInputValue] = useState(savedAddressName || '');
    const [isFocused, setIsFocused] = useState(false);
    const [isInputModified, setIsInputModified] = useState(false);
    
    const suburbInputRef = useRef<HTMLInputElement>(null);
    const addressInputRef = useRef<HTMLInputElement>(null);
    const cache = useRef(new Map<string, Autocomplete[]>());
    const lastRequestTime = useRef(0);

    // Set isValidAddress to true on mount ONLY if both savedLocalityId and savedAddressName exist
    useEffect(() => {
        if (savedLocalityId && savedAddressName) {
            setIsValidAddress(true);
            setIsInputModified(false);
            
            // Trigger the callback to parent component
            onAddressValidityChange(true);
        }
    }, [savedLocalityId, savedAddressName, onAddressValidityChange]);

    // Fetch suburb suggestions
    const fetchSuburbSuggestions = async (query: string) => {
        if (!query.trim()) {
            setSuburbSuggestions([]);
            return;
        }
        try {
            const response = await getApiSearchListingAutocomplete({ query });
            setSuburbSuggestions(response || []);
        } catch (error) {
            console.error('Error fetching suburb suggestions:', error);
            setSuburbSuggestions([]);
        }
    };

    // Handle suburb selection
    const handleSuburbSelect = (suburb: Suggestion) => {
        setSelectedSuburb(suburb);
        setSuburbInput(capitalizeFirstLetters(suburb.localityName));
        setSuburbSuggestions([]);
        setIsSuburbFocused(false);
        
        // Reset street address when suburb changes
        setInputValue('');
        setSelectedAddress(null);
        setIsValidAddress(false);
        setIsInputModified(false);
        
        // Critical: Also notify parent to nullify address selection
        onAddressSelect(null, '', suburb.id);
        
        // Ensure the parent component knows address is invalid after suburb selection
        onAddressValidityChange(false);
        onLocalitySelect(suburb.id, capitalizeFirstLetters(suburb.localityName));
    };

    // Street address autocomplete with selected suburb
    const fetchSuggestions = async (query: string) => {
        if (!selectedSuburb) {
            setSuggestions([]);
            setIsValidAddress(false);
            return;
        }

        if (isLoading || query === lastQuery) return;

        const cachedResult = cache.current.get(query);
        if (cachedResult) {
            setSuggestions(cachedResult.slice(0, 3));
            checkExactMatch(query, cachedResult);
            return;
        }

        const now = Date.now();
        if (now - lastRequestTime.current < 500) return;

        setIsLoading(true);
        setLastQuery(query);
        lastRequestTime.current = now;

        try {
            const result = await getApiSearchAutocomplete({ 
                typedAddress: query, 
                locality: selectedSuburb.id 
            });
            setSuggestions(result.slice(0, 3));
            cache.current.set(query, result);
            setError(null);
            setIsFirstAttempt(false);
            checkExactMatch(query, result);
        } catch (err) {
            setError('Failed to fetch suggestions');
            setSuggestions([]);
            setIsValidAddress(false);
        } finally {
            setIsLoading(false);
        }
    };

    const checkExactMatch = (query: string, currentSuggestions: Autocomplete[]) => {
        const normalizedQuery = query.trim();
        
        if (normalizedQuery.length === 0) {
            setSuggestions([]);
            setIsValidAddress(false);
            onAddressValidityChange(false);
            return;
        }
    
        // Only add user input as suggestion if we have no autocomplete suggestions
        if (currentSuggestions.length === 0 && normalizedQuery.length > 3) {
            const capitalizedAddress = normalizedQuery
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ');
    
            // For manual entries, we need to use an empty string instead of null
            // since the Autocomplete type requires id to be a string
            // The backend will handle empty string IDs correctly
            setSuggestions([{
                id: "",
                address: capitalizedAddress
            }]);
            
            console.log("Added manual entry suggestion with empty string id for:", capitalizedAddress);
            // Address is only valid when explicitly selected from dropdown
        } else {
            setSuggestions(currentSuggestions);
            
            // Check for exact match but don't set validity - user still needs to select
            const exactMatch = currentSuggestions.some(suggestion => 
                suggestion.address.toLowerCase() === normalizedQuery.toLowerCase()
            );
            
            if (exactMatch) {
                console.log("Found exact match in suggestions, but waiting for user selection");
            }
        }
    };

    useEffect(() => {
        if (isFocused) {
            const timer = setTimeout(() => {
                fetchSuggestions(inputValue);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [inputValue, isFocused]);

    // Handle suburb input changes
    const handleSuburbInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSuburbInput(value);
        if (selectedSuburb) {
            setSelectedSuburb(null);
        }
        fetchSuburbSuggestions(value);
    };

    // Handle street address input
    const handleAddressInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);
        setIsInputModified(true);
        
        if (value.length === 0) {
            setIsFirstAttempt(true);
            setIsValidAddress(false);
            onAddressValidityChange(false);
        } else {
            // While typing, address should not be considered valid
            // until explicitly selected from dropdown or confirmed
            setIsValidAddress(false);
            onAddressValidityChange(false);
        }
    };

    const handleAddressSelect = (suggestion: Autocomplete) => {
        setInputValue(suggestion.address);
        onAddressSelect(
            suggestion.id, 
            suggestion.address,
            selectedSuburb?.id || savedLocalityId || '' // Pass locality ID
        );
        setSelectedAddress(suggestion);
        setSuggestions([]);
        setIsFocused(false);
        setIsInputModified(false);
        setIsValidAddress(true);
        
        // Explicitly notify parent that address is valid when selected from dropdown
        onAddressValidityChange(true);
        
        if (addressInputRef.current) {
            addressInputRef.current.blur();
        }
    };

    useEffect(() => {
        // We now handle explicit validity changes in the specific handlers
        // This effect is primarily for debugging and catch-all cases
        if (!selectedSuburb) {
            // If no suburb selected, address is always invalid
            onAddressValidityChange(false);
        }
        
        console.log("Address validity state:", {
            hasSuburb: !!selectedSuburb,
            hasAddress: !!inputValue,
            explicitlyValidAddress: isValidAddress,
            isInputModified,
            addressLength: inputValue?.length
        });
    }, [selectedSuburb, onAddressValidityChange]);

    return (
        <div className="w-full max-w-[1000px] mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <h1 className="block text-2xl font-bold text-gray-800 sm:text-4xl mb-5">Where is it located?</h1>
            <p className="mt text-lg text-gray-800 dark:text-neutral-400 mb-12">
                Your address is not shared, only your approximate location is used.
            </p>
            
            {/* Suburb Selection */}
            <div className="mb-6">
                <label htmlFor="suburb-autocomplete" className="block text-sm font-medium text-gray-700 mb-2">
                    Enter your suburb
                </label>
                <div className="relative">
                    <div className="relative flex items-center">
                        <MapPin className="absolute left-3 h-5 w-5 text-gray-400" />
                        <input
                            ref={suburbInputRef}
                            type="text"
                            id="suburb-autocomplete"
                            className="py-3 pl-10 pr-4 block w-full border border-gray-200 rounded-lg text-base focus:border-blue-500 focus:ring-blue-500"
                            placeholder="Enter suburb"
                            value={suburbInput}
                            onChange={handleSuburbInputChange}
                            onFocus={() => setIsSuburbFocused(true)}
                            onBlur={() => setTimeout(() => setIsSuburbFocused(false), 200)}
                            autoComplete="off"
                        />
                        {selectedSuburb && (
                            <button
                                onClick={() => {
                                    setSelectedSuburb(null);
                                    setSuburbInput('');
                                    setInputValue('');
                                    setSelectedAddress(null);
                                    setIsValidAddress(false);
                                    onAddressSelect(null, '', '');
                                    onAddressValidityChange(false);
                                    onLocalitySelect(null, '');
                                }}
                                className="absolute right-3 text-gray-400 hover:text-gray-600"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        )}
                    </div>
                    
                    {isSuburbFocused && suburbSuggestions.length > 0 && (
                        <div className="absolute z-10 w-full bg-white border border-gray-200 mt-1 rounded-lg shadow-lg">
                            {suburbSuggestions.map((suggestion) => (
                                <div
                                    key={suggestion.id}
                                    className="p-3 hover:bg-gray-100 cursor-pointer"
                                    onClick={() => handleSuburbSelect(suggestion)}
                                >
                                    <span className="text-gray-700">
                                        {capitalizeFirstLetters(suggestion.localityName)}, {suggestion.state} {suggestion.postcode}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Street Address Input */}
            <div className="mb-10 lg:mb-0 lg:col-span-6 lg:col-start-8 lg:order-2">
                <label htmlFor="address-autocomplete" className="block text-sm font-medium text-gray-700 mb-2">
                    Enter your address
                </label>
                <div className="relative">
                    <input
                        ref={addressInputRef}
                        type="text"
                        id="address-autocomplete"
                        className={`py-3 px-4 block w-full border border-gray-200 rounded-lg text-base focus:border-blue-500 focus:ring-blue-500 ${!selectedSuburb ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        placeholder={selectedSuburb ? "Enter address" : "Please select a suburb first"}
                        value={inputValue}
                        onChange={handleAddressInputChange}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setTimeout(() => setIsFocused(false), 200)}
                        disabled={!selectedSuburb}
                        autoComplete="off"
                    />
                    {isFocused && selectedSuburb && (suggestions.length > 0 || isLoading || error) && (
                        <div className="absolute z-10 w-full bg-white border border-gray-200 mt-1 rounded-lg shadow-lg">
                            {isLoading && isFirstAttempt ? (
                                <div className="p-2 text-sm text-gray-500">Loading suggestions...</div>
                            ) : (
                                <>
                                    {suggestions.map((suggestion) => (
                                        <div
                                            key={suggestion.id || 'empty-id-entry'}
                                            className="p-2 hover:bg-gray-100 cursor-pointer"
                                            onClick={() => handleAddressSelect(suggestion)}
                                        >
                                            {suggestion.address.toUpperCase()}
                                        </div>
                                    ))}
                                    {error && <div className="p-2 text-sm text-red-500">{error}</div>}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <p className="mt text-sm text-gray-800 dark:text-neutral-400 mt-4 ml-4">
                Do not include unit numbers in your address.
            </p>
        </div>
    );
};

export default AddressStep;